import type { ImageViewerSlide } from '@skylabs-digital/backoffice-kit';
import type { FistoFile, FistoVariant } from '../../schemas/file.schema';
import { formatBytes } from '../apps/AppList';

export const isImage = (f: Pick<FistoFile, 'contentType'>): boolean =>
  f.contentType.toLowerCase().startsWith('image/');

/** La variante de menos píxeles: la que conviene como miniatura en una fila. */
export function smallestVariant(f: Pick<FistoFile, 'variants'>): FistoVariant | undefined {
  return [...f.variants].sort((a, b) => a.width * a.height - b.width * b.height)[0];
}

/** URL para la miniatura de una fila: la variante más chica, si no el original. */
export function thumbUrl(f: FistoFile): string | null {
  if (!isImage(f)) return null;
  return smallestVariant(f)?.url ?? f.url;
}

/**
 * La hora de la toma como la escribió la cámara, con su zona si Fisto la conoce
 * (FIS-META-007): `2026-05-01 10:20:30 -03:00`. Un backend viejo sólo manda `takenAt`.
 */
export function takenOf(f: Pick<FistoFile, 'image'>): string | null {
  const image = f.image;
  if (!image) return null;
  if (image.takenAtLocal) {
    const wall = image.takenAtLocal.replace('T', ' ');
    return image.takenAtOffset ? `${wall} ${image.takenAtOffset}` : wall;
  }
  return image.takenAt ? new Date(image.takenAt).toLocaleString() : null;
}

/** El visor recibe [original, ...variantes]; las URLs ya vienen resueltas en el listado. */
export function toSlides(f: FistoFile): ImageViewerSlide[] {
  const visibility = f.visibility === 'PUBLIC' ? 'Public' : 'Private';
  const camera = f.image?.camera
    ? [f.image.camera.make, f.image.camera.model].filter(Boolean).join(' ')
    : '';
  const original: ImageViewerSlide = {
    id: f.id,
    src: f.url,
    thumbSrc: smallestVariant(f)?.url ?? undefined,
    title: f.filename,
    subtitle: 'Original',
    width: f.image?.width,
    height: f.image?.height,
    details: [
      { label: 'Path', value: f.path },
      { label: 'Format', value: f.contentType },
      { label: 'Size', value: formatBytes(f.size) },
      { label: 'Visibility', value: visibility },
      // Lo que Fisto calculó en el worker (content identity): sólo si existe.
      ...(f.sha256 ? [{ label: 'SHA-256', value: `${f.sha256.slice(0, 12)}…` }] : []),
      ...(takenOf(f) ? [{ label: 'Taken', value: takenOf(f) }] : []),
      ...(camera ? [{ label: 'Camera', value: camera }] : []),
      ...(f.image?.gps
        ? [
            {
              label: 'Location',
              value: `${f.image.gps.latitude.toFixed(5)}, ${f.image.gps.longitude.toFixed(5)}`,
            },
          ]
        : []),
    ],
  };
  const variants = f.variants.map<ImageViewerSlide>((v) => ({
    id: `${f.id}@${v.name}`,
    src: v.url,
    thumbSrc: v.url ?? undefined,
    title: f.filename,
    subtitle: v.name,
    width: v.width,
    height: v.height,
    details: [
      { label: 'Variant', value: v.name },
      { label: 'Format', value: v.contentType },
      { label: 'Size', value: formatBytes(v.size) },
      { label: 'Visibility', value: visibility },
    ],
  }));
  return [original, ...variants];
}

/** Nombre de variante de una slide del visor (undefined = original). */
export function variantOf(f: FistoFile, slide: ImageViewerSlide): string | undefined {
  return slide.id === f.id ? undefined : slide.subtitle;
}
